import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { defaultButtonClasses } from "@/constants/ui";
import { useLocationQuery } from "@/hooks/useLocationQuery";
import { useRoomQuery } from "@/hooks/useRoomQuery";
import TimeAdjuster from "../TimeAdjuster";
import { useChangeBookingFlow } from "./ChangeBookingFlowProvider";

type DurationProps = {
  onClose?: () => void;
};

const Duration = ({ onClose }: DurationProps) => {
  const { booking, resetFlow, navigateTo, setNewStartTime, setNewEndTime } = useChangeBookingFlow();
  const [pending, setPending] = useState<{ startTime: string; endTime: string } | null>(null);

  // Fetch location and room data if not present on booking
  const { data: fetchedLocation, isLoading: locationLoading } = useLocationQuery(
    booking?.location ? undefined : booking?.locationId,
  );
  const { data: fetchedRoom, isLoading: roomLoading } = useRoomQuery(booking?.room ? undefined : booking?.roomId);

  // Use fetched data if booking doesn't have location/room populated
  const location = booking?.location ?? fetchedLocation ?? null;
  const room = booking?.room ?? fetchedRoom ?? null;

  const handleNext = () => {
    if (!pending) return;
    setNewStartTime(pending.startTime);
    setNewEndTime(pending.endTime);
    navigateTo("duration-confirmation");
  };

  const handleCancel = () => {
    resetFlow();
    onClose?.();
  };

  if (!booking) {
    return (
      <div className="p-6">
        <p className="text-red-600">Error: No booking data available</p>
        <Button onClick={handleCancel} className="mt-4">
          Close
        </Button>
      </div>
    );
  }

  // Show loading state while fetching location/room data
  if (locationLoading || roomLoading) {
    return (
      <div className="min-h-[90vh] lg:min-h-[70vh] w-full flex flex-col bg-white rounded-xl my-4">
        <div className="flex items-center justify-center h-64">
          <Spinner className="size-6 mr-2" />
          <span className="text-lg">Loading booking data...</span>
        </div>
      </div>
    );
  }

  // Show error if location or room couldn't be fetched
  if (!location || !room) {
    return (
      <div className="p-6">
        <p className="text-red-600">Error: Could not load location or room data</p>
        <Button onClick={handleCancel} className="mt-4">
          Close
        </Button>
      </div>
    );
  }

  const picker = <TimeAdjuster booking={booking} location={location} room={room} onChange={setPending} />;

  const actions = (
    <div className="flex flex-col gap-2 mt-8 flex-shrink-0">
      <Button className={`w-full ` + defaultButtonClasses} onClick={handleNext} disabled={!pending}>
        Next
      </Button>
      <Button variant="ghost" className="w-full text-gray-600" onClick={handleCancel}>
        Close
      </Button>
    </div>
  );

  return (
    <div className="min-h-[90vh] lg:min-h-[70vh] w-full flex flex-col bg-white rounded-xl my-4">
      {/* Header - Mobile only */}
      <div className="lg:hidden p-4 py-6 flex flex-col circle-pattern-background circle-pattern-indigo-50">
        <h2 className="text-2xl font-semibold mb-2">Change Time</h2>
        <p className="text-gray-600 text-xs">Adjust when your booking starts and ends in 30-minute steps.</p>
      </div>

      {/* Desktop grid layout */}
      <div className="hidden lg:grid lg:grid-cols-3 grow gap-1">
        {/* Left column - Information */}
        <div className="p-6 lg:rounded-lg flex flex-col circle-pattern-background circle-pattern-indigo-50">
          <h2 className="text-3xl font-semibold mb-4">Change Time</h2>
          <p className="mb-2 text-gray-600 text-sm">
            Adjust when your booking starts and ends in 30-minute steps. Changing the end time changes the
            duration.
          </p>
          <div className="mt-4 space-y-2">
            <div className="p-2 bg-white rounded border border-gray-200">
              <p className="text-xs font-semibold text-gray-700">Current Booking:</p>
              <p className="text-sm text-gray-600">
                {booking.date.toDateString()} at {booking.startTime} - {booking.endTime}
              </p>
            </div>
          </div>
        </div>

        {/* Right column - Time Adjuster */}
        <div className="bg-gray-50 rounded-lg lg:rounded-r-xl lg:col-span-2 p-6 border-l border-gray-200 shadow-sm flex flex-col max-h-[70vh]">
          <div className="flex-1 overflow-y-auto min-h-0">{picker}</div>
          {actions}
        </div>
      </div>

      {/* Mobile content area */}
      <div className="lg:hidden flex-1 bg-gray-50 p-6 flex flex-col max-h-[80vh]">
        <div className="flex-1 overflow-y-auto min-h-0">{picker}</div>
        <div className="pb-4">{actions}</div>
      </div>
    </div>
  );
};

export default Duration;
